import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDifficultWords, recordProgress, Content } from '../services/api';
import { speak } from '../services/audio';
import './DifficultWords.css';

const DifficultWords: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Content[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  useEffect(() => {
    const loadItems = async () => {
      try {
        const data = await getDifficultWords(15);
        setItems(data);
      } catch (err) {
        console.error('Failed to load difficult words:', err);
      } finally {
        setLoading(false);
      }
    };
    loadItems();
  }, []);

  const handleReveal = () => {
    setShowAnswer(true);
    speak(items[currentIndex].english);
  };

  const handleAnswer = async (correct: boolean) => {
    const item = items[currentIndex];
    await recordProgress(item.id, correct);
    
    setScore(prev => ({
      correct: prev.correct + (correct ? 1 : 0),
      total: prev.total + 1
    }));

    if (currentIndex < items.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowAnswer(false);
    } else {
      setCompleted(true);
    }
  };

  if (loading) return <div className="loading">Ładowanie...</div>;

  if (items.length === 0) {
    return (
      <div className="no-difficult fade-in">
        <div className="no-difficult-icon">🎉</div>
        <h2>Brak trudnych słówek!</h2>
        <p>Najpierw naucz się słówek w lekcjach lub grach, a potem wróć tutaj.</p>
        <button className="btn btn-primary btn-large" onClick={() => navigate('/lessons')}>
          Idź do lekcji 📚
        </button>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="difficult-complete fade-in">
        <div className="complete-icon">💪</div>
        <h1>Dobra robota!</h1>
        <p>Powtórzyłeś {items.length} trudnych słówek</p>
        <p>Wynik: {score.correct} / {score.total}</p>
        <button className="btn btn-primary btn-large" onClick={() => navigate('/lessons')}>
          Wróć do lekcji
        </button>
      </div>
    );
  }

  const currentItem = items[currentIndex];

  return (
    <div className="difficult-page fade-in">
      <div className="difficult-header">
        <h2>🔥 Trudne słówka</h2>
        <div className="progress-indicator">{currentIndex + 1} / {items.length}</div>
      </div>

      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${((currentIndex + 1) / items.length) * 100}%` }}
        />
      </div>

      <div className="difficult-card">
        <div className="polish-word-large">{currentItem.polish}</div>
        
        {!showAnswer ? (
          <button className="btn btn-primary btn-large" onClick={handleReveal}>
            Pokaż odpowiedź 👀
          </button>
        ) : (
          <>
            <div className="english-answer">
              {currentItem.english}
              <button className="speak-btn" onClick={() => speak(currentItem.english)}>🔊</button>
            </div>
            <p className="self-check">Czy wiedziałeś?</p>
            <div className="answer-buttons">
              <button className="btn btn-danger" onClick={() => handleAnswer(false)}>
                ❌ Nie
              </button>
              <button className="btn btn-success" onClick={() => handleAnswer(true)}>
                ✅ Tak
              </button>
            </div>
          </>
        )}
      </div>

      <div className="memorization-info">
        <span className="mem-label">Poziom zapamiętania:</span>
        <span className="mem-level">{(currentItem as any).memorization_level || 0}</span>
      </div>
    </div>
  );
};

export default DifficultWords;

